<template>
	<div v-if="stacked" class="d-flex flex-column ga-4">
		<v-chip v-if="free" color="success" variant="flat" size="small" class="align-self-start">{{ freeLabel }}</v-chip>
		<v-chip v-else-if="negotiatedLabel" size="small" color="primary" prepend-icon="circle" append-icon="chevron_right" class="align-self-start" @click="panelOpen = true">{{ negotiatedLabel }}</v-chip>
		<div class="promo-presence-toggle">
			<v-btn prepend-icon="visibility" :color="present === PRESENCE.PRESENT ? 'success' : undefined" :variant="present === PRESENCE.PRESENT ? 'tonal' : 'outlined'" :disabled="!editable" @click="update('present', PRESENCE.PRESENT)">{{ t('Present') }}</v-btn>
			<v-btn prepend-icon="visibility_off" :color="present === PRESENCE.ABSENT ? 'error' : undefined" :variant="present === PRESENCE.ABSENT ? 'tonal' : 'outlined'" :disabled="!editable" @click="update('present', PRESENCE.ABSENT)">{{ t('Absent') }}</v-btn>
		</div>
		<div>
			<div class="text-body-small text-medium-emphasis mb-1">{{ t('VolumeStated') }}</div>
			<div class="d-flex align-center ga-2">
				<v-text-field :key="`volume-${resetKey}`" :model-value="row?.volume ?? 0" type="number" min="0" inputmode="numeric" variant="outlined" density="compact" hide-details :disabled="!active" class="flex-grow-1" @change="(event) => update('volume', event.target.value)" />
				<v-select :menu-props="menuProps" :model-value="row?.productContainerId || 0" :items="containerItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!active" class="flex-grow-1" @update:model-value="(value) => update('container', value)" />
				<v-chip size="small" color="primary" label>{{ format.uc(target.statedUC) }}</v-chip>
			</div>
			<div class="d-flex align-center ga-2 mt-1 text-body-small text-medium-emphasis">
				{{ t('PromoTarget') }} {{ format.uc(target.targetUC) }}
				<v-chip v-if="present === PRESENCE.PRESENT && target.targetUC" size="x-small" :color="target.reached ? 'success' : 'warning'" label>{{ target.reached ? '✓' : `${format.number(target.gap)} ${t('UC')}` }}</v-chip>
			</div>
		</div>
		<div v-if="!free">
			<v-text-field :key="`price-${resetKey}`" :model-value="row?.statedValue || ''" type="number" min="0" step="0.01" inputmode="decimal" :label="t('ProductPriceStated')" variant="outlined" density="compact" hide-details :disabled="!active" @change="(event) => update('price', event.target.value)" />
			<div class="text-body-small text-medium-emphasis mt-1">{{ t('PromoNegotiatedRef') }} {{ format.money(negotiated?.statedValue || price) }}</div>
		</div>
		<div>
			<v-select :menu-props="menuProps" :model-value="row?.locationPromoId || 0" :items="locationItems" item-title="label" item-value="id" :label="t('Location')" variant="outlined" density="compact" hide-details :disabled="!active" @update:model-value="(value) => update('location', value)" />
			<div class="text-body-small text-medium-emphasis mt-1">{{ t('PromoNegotiatedRef') }} {{ labelOf(snapshot.refs.locations, negotiated?.locationPromoId) }}</div>
		</div>
		<div>
			<v-select :menu-props="menuProps" :model-value="row?.locationHighlightId || 0" :items="highlightItems" item-title="label" item-value="id" :label="t('LocationHighlight')" variant="outlined" density="compact" hide-details :disabled="!active" @update:model-value="(value) => update('highlight', value)" />
			<div class="text-body-small text-medium-emphasis mt-1">{{ t('PromoNegotiatedRef') }} {{ labelOf(snapshot.refs.highlights, negotiated?.locationHighlightId) }}</div>
		</div>
		<v-badge v-if="!free" :model-value="additionalCount > 0" :content="additionalCount" color="warning" class="align-self-start">
			<v-btn :prepend-icon="additionalAction.icon" :disabled="!active && !additionalCount" @click="openAdditional">{{ t(additionalAction.label) }}</v-btn>
		</v-badge>
	</div>
	<tr v-else>
		<td class="position-relative">
			<v-sheet v-if="status" :color="status" width="4" class="position-absolute top-0 bottom-0 left-0" />
			<div v-if="free" class="d-flex flex-column align-start ga-1 py-2 ps-4 pe-2">
				<v-chip color="success" variant="flat" size="small">{{ product?.label }}</v-chip>
				<span class="text-body-small text-medium-emphasis">{{ freeLabel }}</span>
			</div>
			<div v-else class="py-2 ps-4 pe-2">
				<ProductLabel :params="{ value: product?.label || '', imageThumbnailUid: product?.imageThumbnailUid, productId, avatarSize: 32 }" />
				<v-chip v-if="negotiatedLabel" size="small" color="primary" prepend-icon="circle" append-icon="chevron_right" class="mt-1" @click="panelOpen = true">{{ negotiatedLabel }}</v-chip>
			</div>
		</td>
		<td>
			<div class="d-flex ga-1 pa-2">
				<v-btn icon="visibility" size="small" :color="present === PRESENCE.PRESENT ? 'success' : undefined" :variant="present === PRESENCE.PRESENT ? 'tonal' : 'outlined'" :disabled="!editable" @click="update('present', PRESENCE.PRESENT)" />
				<v-btn icon="visibility_off" size="small" :color="present === PRESENCE.ABSENT ? 'error' : undefined" :variant="present === PRESENCE.ABSENT ? 'tonal' : 'outlined'" :disabled="!editable" @click="update('present', PRESENCE.ABSENT)" />
			</div>
		</td>
		<td>
			<div class="d-flex align-center ga-2 px-2 pt-2">
				<v-text-field :key="`volume-${resetKey}`" :model-value="row?.volume ?? 0" type="number" min="0" variant="outlined" density="compact" hide-details :disabled="!active" width="84" @change="(event) => update('volume', event.target.value)" />
				<v-select :menu-props="menuProps" :model-value="row?.productContainerId || 0" :items="containerItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!active" width="120" @update:model-value="(value) => update('container', value)" />
				<v-chip size="small" color="primary" label>{{ format.uc(target.statedUC) }}</v-chip>
			</div>
			<div class="d-flex align-center ga-2 mt-1 px-2 pb-2 text-body-small text-medium-emphasis">
				{{ t('PromoTarget') }} {{ format.uc(target.targetUC) }}
				<v-chip v-if="present === PRESENCE.PRESENT && target.targetUC" size="x-small" :color="target.reached ? 'success' : 'warning'" label>{{ target.reached ? '✓' : `${format.number(target.gap)} ${t('UC')}` }}</v-chip>
			</div>
		</td>
		<td>
			<div v-if="!free" class="pa-2">
				<v-text-field :key="`price-${resetKey}`" :model-value="row?.statedValue || ''" type="number" min="0" step="0.01" variant="outlined" density="compact" hide-details :disabled="!active" width="110" @change="(event) => update('price', event.target.value)" />
				<div class="text-body-small text-medium-emphasis mt-1">{{ t('PromoNegotiatedRef') }} {{ format.money(negotiated?.statedValue || price) }}</div>
			</div>
		</td>
		<td>
			<div class="pa-2">
				<v-select :menu-props="menuProps" :model-value="row?.locationPromoId || 0" :items="locationItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!active" width="160" @update:model-value="(value) => update('location', value)" />
				<div class="text-body-small text-medium-emphasis mt-1">{{ t('PromoNegotiatedRef') }} {{ labelOf(snapshot.refs.locations, negotiated?.locationPromoId) }}</div>
			</div>
		</td>
		<td>
			<div class="pa-2">
				<v-select :menu-props="menuProps" :model-value="row?.locationHighlightId || 0" :items="highlightItems" item-title="label" item-value="id" variant="outlined" density="compact" hide-details :disabled="!active" width="140" @update:model-value="(value) => update('highlight', value)" />
				<div class="text-body-small text-medium-emphasis mt-1">{{ t('PromoNegotiatedRef') }} {{ labelOf(snapshot.refs.highlights, negotiated?.locationHighlightId) }}</div>
			</div>
		</td>
		<td>
			<div v-if="!free" class="pa-2">
				<v-badge :model-value="additionalCount > 0" :content="additionalCount" color="warning">
					<v-btn :prepend-icon="additionalAction.icon" size="small" :disabled="!active && !additionalCount" @click="openAdditional">{{ t(additionalAction.label) }}</v-btn>
				</v-badge>
			</div>
		</td>
	</tr>
	<template v-if="!free">
		<PromoAdditionalPresenceDialog v-model="additionalOpen" :campaign="campaign" :offer="offer" :offer-ref="offerRef" :product-id="productId" kind="stated" :editable="active" />
		<PromoNegotiatedPanel v-model="panelOpen" :campaign="campaign" :offer="offer" :offer-ref="offerRef" :product-id="productId" />
	</template>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { usePromoStep } from '@/composables/usePromoStep';
import { STATED, containersOf, getProduct } from '@/utils/domain/promo/promoModel';
import { isProductEditable, negotiatedRowOf } from '@/utils/domain/promo/promoNego';
import { freeRowOf } from '@/utils/domain/promo/promoFree';
import { PRESENCE, negotiatedSummary, productTarget, statedRowOf } from '@/utils/domain/promo/promoStatement';
import { additionalOf, canOpenAdditional } from '@/utils/domain/promo/promoAdditional';
import { productStatus } from '@/utils/domain/promo/promoStatus';
import { priceOf } from '@/utils/domain/promo/promoValues';
import { isStatementBlocked } from '@/utils/domain/promo/promoForecast';
import ProductLabel from '@/components/common/ProductLabel.vue';
import PromoAdditionalPresenceDialog from './PromoAdditionalPresenceDialog.vue';
import PromoNegotiatedPanel from './PromoNegotiatedPanel.vue';

const props = defineProps({
	campaign: { type: Object, required: true },
	offer: { type: Object, required: true },
	offerRef: { type: Object, required: true },
	productId: { type: Number, required: true },
	free: { type: Boolean, default: false },
	stacked: { type: Boolean, default: false },
});

const { snapshot, ops, format, notify, t } = usePromoStep();
const additionalOpen = ref(false);
const resetKey = ref(0);
const overlayTarget = inject('overlayTarget', null);
const menuProps = { attach: overlayTarget || undefined };
const panelOpen = ref(false);

const product = computed(() => getProduct(snapshot.value, props.productId));
const row = computed(() => statedRowOf(snapshot.value, props.offerRef, props.productId, props.free));
const negotiated = computed(() => (props.free ? freeRowOf(snapshot.value, props.offerRef, props.productId) : negotiatedRowOf(snapshot.value, props.offerRef, props.productId)));
const summary = computed(() => negotiatedSummary(snapshot.value, props.offerRef, props.productId));
const present = computed(() => row.value?.present || PRESENCE.NONE);
const editable = computed(() => isProductEditable(snapshot.value, props.campaign, props.offerRef, props.productId) && !isStatementBlocked(snapshot.value, props.campaign, props.offerRef));
const active = computed(() => editable.value && present.value === PRESENCE.PRESENT);
const status = computed(() => productStatus(snapshot.value, props.campaign, props.offer.id, props.productId, props.offerRef.offerKey, props.free));
const target = computed(() => productTarget(snapshot.value, props.offerRef, props.productId, props.free));
const additionalCount = computed(() => additionalOf(snapshot.value, props.offerRef, props.productId, STATED).length);
const additionalAction = computed(() => (active.value ? { icon: 'add', label: 'PromoAddPresence' } : { icon: 'add_location_alt', label: 'AdditionnalPresence' }));
const price = computed(() => priceOf(props.campaign, props.productId));

const empty = computed(() => ({ id: 0, label: '...' }));
const containerItems = computed(() => [empty.value, ...containersOf(snapshot.value, props.offer, props.productId)]);
const locationItems = computed(() => [empty.value, ...snapshot.value.refs.locations]);
const highlightItems = computed(() => [empty.value, ...snapshot.value.refs.highlights]);

const containerLabel = (id) => snapshot.value.refs.containers.find((c) => c.id === id)?.label || '';
const labelOf = (items, id) => items.find((item) => item.id === id)?.label || '—';

const freeLabel = computed(() => {
	const volume = Number(negotiated.value?.volume) || 0;
	return volume ? `${t('PromoFree')} · ${format.number(volume)} ${containerLabel(negotiated.value.productContainerId)}` : t('PromoFree');
});

const negotiatedLabel = computed(() => {
	if (!summary.value) return '';
	const quantity = summary.value.volume !== null ? `${format.number(summary.value.volume)} ${containerLabel(summary.value.containerId)}` : format.uc(summary.value.totalUC);
	return `${t('Nego')} · ${quantity}`;
});

function update(field, value) {
	if (!ops.statement({ ...props.offerRef, productId: props.productId, free: props.free, field, value })) resetKey.value += 1;
}

function openAdditional() {
	const check = active.value ? canOpenAdditional(snapshot.value, props.offerRef, props.productId, STATED) : { ok: true };
	if (!check.ok) return notify(check.error);
	additionalOpen.value = true;
}
</script>
